import { AppFileRouterEndpoint } from 'src/interfaces/AppFileRouter';

import { getUploadAcceptConfig } from './uploadAccept';

const EXTENSION_TO_ENDPOINT: Record<string, AppFileRouterEndpoint> = {
  '.jpg': 'image',
  '.jpeg': 'image',
  '.png': 'image',
  '.gif': 'image',
  '.webp': 'image',
  '.svg': 'image',
  '.pdf': 'document',
  '.doc': 'document',
  '.docx': 'document',
  '.gpx': 'trackMap',
  '.xml': 'trackMap',
};

const ALL_ENDPOINTS: AppFileRouterEndpoint[] = ['image', 'document', 'trackMap'];

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex === -1) {
    return '';
  }
  return fileName.slice(dotIndex).toLowerCase();
}

function matchesAcceptMime(file: File, mimePattern: string): boolean {
  if (mimePattern.endsWith('/*')) {
    const prefix = mimePattern.slice(0, -1);
    return file.type.startsWith(prefix);
  }
  return file.type === mimePattern;
}

export function resolveEndpointForFile(file: File): AppFileRouterEndpoint | null {
  const extension = getFileExtension(file.name);
  if (extension && EXTENSION_TO_ENDPOINT[extension]) {
    return EXTENSION_TO_ENDPOINT[extension];
  }

  for (const endpoint of ALL_ENDPOINTS) {
    const acceptConfig = getUploadAcceptConfig(endpoint);
    const matches = Object.entries(acceptConfig).some(([mimePattern, extensions]) => {
      const mimeMatch = file.type ? matchesAcceptMime(file, mimePattern) : false;
      const extensionMatch = extension ? extensions.includes(extension) : false;
      return mimeMatch || extensionMatch;
    });
    if (matches) {
      return endpoint;
    }
  }

  return null;
}

export function getMergedAcceptConfig(
  endpoints: AppFileRouterEndpoint[],
): Record<string, string[]> {
  return endpoints.reduce<Record<string, string[]>>((merged, endpoint) => {
    const config = getUploadAcceptConfig(endpoint);
    Object.entries(config).forEach(([mime, extensions]) => {
      merged[mime] = [...new Set([...(merged[mime] ?? []), ...extensions])];
    });
    return merged;
  }, {});
}

export function groupFilesByEndpoint(
  files: File[],
): Map<AppFileRouterEndpoint, File[]> {
  const groups = new Map<AppFileRouterEndpoint, File[]>();

  files.forEach(file => {
    const endpoint = resolveEndpointForFile(file);
    if (!endpoint) {
      return;
    }
    const existing = groups.get(endpoint) ?? [];
    groups.set(endpoint, [...existing, file]);
  });

  return groups;
}

export const UPLOAD_ENDPOINTS: AppFileRouterEndpoint[] = ALL_ENDPOINTS;
